import type { FC } from 'react';
import React, { useEffect } from 'react';
import { Container } from '@mui/material';
import {
  Button,
  HeaderBreadcrumbs,
  Iconify,
  Stack,
} from '@pflegenavi/web-components';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { YEAR_MONTH_FORMAT } from '@pflegenavi/shared/constants';
import { useLocale } from '@pflegenavi/frontend/localization';
import { useTranslation } from 'react-i18next';
import { useInitializeResidents } from '../hooks/useUnitializeResidents';
import { ServiceProviderReceiptBatchOverviewTable } from '../components/receipt-batch-table/ServiceProviderReceiptBatchOverviewTable';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { StripeStateOverview } from '../components/stripe/StripeStateOverview';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';
import {
  useGetDashboardPhoenix,
  useServiceProvider,
} from '@pflegenavi/service-provider-frontend/api';
import { useGetGlobalServiceProviderId } from '../hooks/useGetGlobalServiceProviderId';
import { MangopayStateOverview } from '../components/mangopay/StripeStateOverview';
import { useSnackbar } from 'notistack';
import { BankAccountSetupBanner } from '../components/dashboard/BankAccountSetupBanner';
import { KycVerificationBanner } from '../components/dashboard/KycVerificationBanner';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface DashboardPageProps {}

export const DashboardPage: FC<DashboardPageProps> = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();

  const { data: serviceProvider } = useServiceProvider({
    globalServiceProviderId:
      useGetGlobalServiceProviderId().globalServiceProviderId,
  });

  // Handle return from SCA verification
  useEffect(() => {
    const scaStatus = searchParams.get('sca_status');

    if (scaStatus === 'success') {
      enqueueSnackbar(t('service-provider-app.sca.verification-successful'), {
        variant: 'success',
      });
    } else if (scaStatus === 'failure') {
      enqueueSnackbar(t('service-provider-app.sca.verification-failed'), {
        variant: 'error',
      });
    }

    // Clean up URL parameters
    if (scaStatus) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams, enqueueSnackbar, t]);

  if (serviceProvider?.paymentProcessor === 'mangopay') {
    return <DashboardPageMangopay />;
  } else {
    return <DashboardPageStripe />;
  }
};

export const DashboardPageStripe: FC<DashboardPageProps> = () => {
  const locale = useLocale();
  const { t } = useTranslation();

  const stripeVisualizationEnabled = useFeatureFlagEnabled(
    FeatureFlag.dev_StripeVisualization
  );

  useInitializeResidents();

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('dashboard.main')}
        links={[
          {
            name: t('dashboard.main'),
            href: spAppMainPages.DASHBOARD,
          },
          { name: new Date().toLocaleDateString(locale, YEAR_MONTH_FORMAT) },
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            component={RouterLink}
            // @ts-expect-error param of RouterLink
            to={spAppMainPages.RECEIPT_BATCH_NEW}
          >
            {t('sp-web-app.receipt-batch.new')}
          </Button>
        }
      />
      {stripeVisualizationEnabled ? (
        <Stack gap={3}>
          <StripeStateOverview />
          <ServiceProviderReceiptBatchOverviewTable />
        </Stack>
      ) : (
        <ServiceProviderReceiptBatchOverviewTable />
      )}
    </Container>
  );
};

export const DashboardPageMangopay: FC<DashboardPageProps> = () => {
  const locale = useLocale();
  const { t } = useTranslation();

  useInitializeResidents();

  const { data: dashboard } = useGetDashboardPhoenix();

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('dashboard.main')}
        links={[
          {
            name: t('dashboard.main'),
            href: spAppMainPages.DASHBOARD,
          },
          { name: new Date().toLocaleDateString(locale, YEAR_MONTH_FORMAT) },
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            component={RouterLink}
            // @ts-expect-error param of RouterLink
            to={spAppMainPages.RECEIPT_BATCH_NEW}
          >
            {t('sp-web-app.receipt-batch.new')}
          </Button>
        }
      />
      <BankAccountSetupBanner />
      <KycVerificationBanner />
      <Stack gap={3}>
        <MangopayStateOverview dashboard={dashboard} />
        <ServiceProviderReceiptBatchOverviewTable />
      </Stack>
    </Container>
  );
};
